// Resize Image command â€” resamples the entire document to new dimensions.
// Uses bilinear interpolation for resampling.

import type { Command } from "./Command";
import type { ActionStep } from "../actions/ActionStep";
import type { Document } from "../document/Document";
import type { RasterLayer } from "../document/Layer";
import { findRaster } from "../document/LayerUtils";
import { PixelBuffer } from "../pixel/PixelBuffer";
import { Selection } from "../selection/Selection";
import { scaleSelectionToNewSize } from "./SelectionTransformCommand";

export interface ResizeOptions {
  width: number;
  height: number;
  /** Resampling algorithm. */
  method: "nearest" | "bilinear" | "bicubic";
  /** Whether to preserve aspect ratio. */
  constrainProportions?: boolean;
}

export class ResizeImageCommand implements Command {
  readonly kind = "resize-image";

  constructor(
    private readonly options: ResizeOptions,
    private before?: { width: number; height: number },
  ) {}

  apply(doc: Document): Document {
    let { width: newW, height: newH, method, constrainProportions } = this.options;

    if (!this.before) {
      this.before = { width: doc.width, height: doc.height };
    }

    // If constraining proportions, compute the missing dimension from the aspect ratio
    if (constrainProportions) {
      const aspect = doc.width / doc.height;
      const userW = this.options.width;
      const userH = this.options.height;
      const origW = this.before.width;
      const origH = this.before.height;
      // Determine which dimension the user likely changed (or use width as primary)
      if (userW !== origW) {
        newH = Math.round(userW / aspect);
        newW = userW;
      } else if (userH !== origH) {
        newW = Math.round(userH * aspect);
        newH = userH;
      } else {
        // Both same as original â€” use width as primary
        newH = Math.round(newW / aspect);
      }
    }

    // Resize every raster layer
    for (const layer of collectRasters(doc.layers)) {
      const oldW = layer.pixels.width;
      const oldH = layer.pixels.height;
      const newData = resample(layer.pixels.data, oldW, oldH, newW, newH, method);

      // Scale layer position proportionally
      layer.x = Math.round(layer.x * (newW / oldW));
      layer.y = Math.round(layer.y * (newH / oldH));

      layer.pixels = new PixelBuffer(newW, newH, "rgba8");
      layer.pixels.data.set(newData);
      layer.pixels.markDirty();
    }

    // #69: scale doc.selection to the new canvas dimensions. Without this,
    // the selection mask retains the old (oldW Ã— oldH) size and any consumer
    // that indexes `y * doc.width + x` reads wrong bytes.
    const oldDocW = this.before.width;
    const oldDocH = this.before.height;
    const scaledSel: Selection | null = (oldDocW === newW && oldDocH === newH)
      ? doc.selection
      : scaleSelectionToNewSize(doc.selection, newW, newH);
    (doc as { selection: Selection | null }).selection = scaledSel;

    doc.width = newW;
    doc.height = newH;
    return doc;
  }

  invert(_before: Document, _after: Document): Command {
    if (!this.before) throw new Error("Cannot invert without before state");
    return new ResizeImageCommand({
      width: this.before.width,
      height: this.before.height,
      method: this.options.method,
    }, { width: _after.width, height: _after.height });
  }

  toActionStep(): ActionStep {
    return {
      id: crypto.randomUUID(),
      name: "Image Size",
      command: "image-size",
      params: { ...this.options } as Record<string, unknown>,
      recordable: true,
    };
  }

}

function collectRasters(layers: readonly (RasterLayer | import("../document/Layer").GroupLayer | import("../document/Layer").Layer)[]): RasterLayer[] {
  const result: RasterLayer[] = [];
  for (const l of layers) {
    if (l.kind === "raster") result.push(l);
    else if (l.kind === "group") result.push(...collectRasters(l.children));
  }
  return result;
}

function resample(
  src: Uint8ClampedArray,
  srcW: number,
  srcH: number,
  dstW: number,
  dstH: number,
  method: "nearest" | "bilinear" | "bicubic",
): Uint8ClampedArray {
  const dst = new Uint8ClampedArray(dstW * dstH * 4);

  if (method === "nearest") {
    for (let dy = 0; dy < dstH; dy++) {
      for (let dx = 0; dx < dstW; dx++) {
        const sx = Math.round(dx * srcW / dstW);
        const sy = Math.round(dy * srcH / dstH);
        const si = (Math.min(sy, srcH - 1) * srcW + Math.min(sx, srcW - 1)) * 4;
        const di = (dy * dstW + dx) * 4;
        dst[di] = src[si]!;
        dst[di + 1] = src[si + 1]!;
        dst[di + 2] = src[si + 2]!;
        dst[di + 3] = src[si + 3]!;
      }
    }
    return dst;
  }

  // Bilinear (also used for bicubic as fallback)
  for (let dy = 0; dy < dstH; dy++) {
    for (let dx = 0; dx < dstW; dx++) {
      const fx = dx * srcW / dstW;
      const fy = dy * srcH / dstH;
      const x0 = Math.floor(fx);
      const y0 = Math.floor(fy);
      const x1 = Math.min(x0 + 1, srcW - 1);
      const y1 = Math.min(y0 + 1, srcH - 1);
      const tx = fx - x0;
      const ty = fy - y0;

      const i00 = (y0 * srcW + x0) * 4;
      const i10 = (y0 * srcW + x1) * 4;
      const i01 = (y1 * srcW + x0) * 4;
      const i11 = (y1 * srcW + x1) * 4;
      const di = (dy * dstW + dx) * 4;

      for (let c = 0; c < 4; c++) {
        const top = src[i00 + c]! * (1 - tx) + src[i10 + c]! * tx;
        const bot = src[i01 + c]! * (1 - tx) + src[i11 + c]! * tx;
        dst[di + c] = Math.round(top * (1 - ty) + bot * ty);
      }
    }
  }

  return dst;
}

